import React, { useEffect, useRef } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { useFragment, useLazyLoadQuery } from 'react-relay';
import { trackCustomLink } from '@sparknz/tracking';

import { PrimaryPlanStepQuery } from '__generated__/PrimaryPlanStepQuery.graphql';
import {
  RelatedOfferFragment$data,
  RelatedOfferFragment$key,
} from '__generated__/RelatedOfferFragment.graphql';
import { relatedOfferFragment } from 'components/shop/MobilePlans/fragments/RelatedOfferFragment.fragment';
import { useSelectedPlans } from 'components/shop/MobilePlans/providers';
import {
  MobilePlansDetails,
  RelatedOfferDetails,
  SelectedPlan,
} from 'components/shop/MobilePlans/typings/plans';
import { PlanConfigurationStep } from '../PlanConfiguration/PlanConfiguration';
import SelectQuantityStep from '../SelectQuantityStep';
import AddCompanionsStep from './components/AddCompanionsStep';
import EligibleMessageStep from './components/EligibleMessageStep';
import ReviewSelectionStep from './components/ReviewSelectionStep';
import SelectCompanionQuantityStep from './components/SelectCompanionQuantityStep';
import SelectKidPlanQuantityStep from './components/SelectKidPlanQuantityStep';

const SELECT_KIDS_PLAN_TRACKING_EVENT = 'See kids plan details';
const SELECT_COMPANION_TRACKING_EVENT = 'See companion details';

const query = graphql`
  query PrimaryPlanStepQuery($offerIds: [String!]) {
    productOfferDetails(offerIds: $offerIds) {
      offerDetails {
        ...RelatedOfferFragment
      }
    }
  }
`;

interface AvailableRelatedOffers {
  companionPlan?: RelatedOfferDetails;
  kidsPlan?: RelatedOfferDetails;
}

interface PrimaryPlanStepProps {
  step: PlanConfigurationStep;
  setLayerTitle: (title: string) => void;
  selectedPlan: SelectedPlan;
  relatedOfferIds: readonly string[];
  planCardsProducts: MobilePlansDetails;
  isAddingBundles: boolean;
  navigate(to: PlanConfigurationStep): void;
  addSelectedPlanToCart(): void;
  onCancel(): void;
}

const getRelatedOffers = (offerDetailsList: RelatedOfferFragment$data) => {
  const availableRelatedOffers: AvailableRelatedOffers = {};

  for (const offer of offerDetailsList) {
    for (const category of offer.category ?? []) {
      if (category.id === 'companion_plan') {
        availableRelatedOffers.companionPlan = offer;
        break;
      }
      if (category.id === 'kids_plan') {
        availableRelatedOffers.kidsPlan = offer;
        break;
      }
    }
    if (
      availableRelatedOffers.companionPlan &&
      availableRelatedOffers.kidsPlan
    ) {
      break;
    }
  }

  if (
    !availableRelatedOffers.companionPlan &&
    !availableRelatedOffers.kidsPlan
  ) {
    availableRelatedOffers.companionPlan = offerDetailsList[0];
  }

  return availableRelatedOffers;
};

const getStepLayerTitle = (step: PlanConfigurationStep) => {
  switch (step) {
    case 'SELECT_QUANTITY_BEFORE_ELIGIBLE_MESSAGE':
      return 'Select plan';
    case 'ADD_COMPANION_PLAN_ONLY':
      return 'Add companions';
    case 'SHOW_ELIGIBLE_MESSAGE':
      return 'Add plans';
    case 'SELECT_KIDS_PLAN_QUANTITY':
      return 'Add kids plans';
    case 'SELECT_COMPANION_QUANTITY':
      return 'Add companion plans';
    case 'REVIEW_RELATED_OFFERS_SELECTION':
      return 'Add monthly plans';
    default:
      return '';
  }
};

const PrimaryPlanStep = ({
  step,
  selectedPlan,
  relatedOfferIds,
  planCardsProducts,
  isAddingBundles,
  navigate,
  setLayerTitle,
  addSelectedPlanToCart,
  onCancel,
}: PrimaryPlanStepProps) => {
  const previousStep = useRef<PlanConfigurationStep | null>(null);
  const {
    productOfferDetails: { offerDetails },
  } = useLazyLoadQuery<PrimaryPlanStepQuery>(query, {
    offerIds: relatedOfferIds,
  });

  const handleNavigate = (
    to: PlanConfigurationStep,
    trackingEvent?: string,
  ) => {
    previousStep.current = step;
    if (trackingEvent) {
      trackCustomLink(trackingEvent);
    }
    navigate(to);
  };

  const handleNavigateToSelectKidsPlanQuantity = () => {
    handleNavigate(
      'SELECT_KIDS_PLAN_QUANTITY',
      SELECT_KIDS_PLAN_TRACKING_EVENT,
    );
  };
  const handleNavigateToSelectCompanionQuantity = () => {
    handleNavigate(
      'SELECT_COMPANION_QUANTITY',
      SELECT_COMPANION_TRACKING_EVENT,
    );
  };

  const handleNavigateToReviewRelatedOffersSelection = () => {
    handleNavigate('REVIEW_RELATED_OFFERS_SELECTION');
  };

  const offerDetailsList = useFragment<RelatedOfferFragment$key>(
    relatedOfferFragment,
    offerDetails,
  );

  const availableRelatedOffers = getRelatedOffers(offerDetailsList);
  const { companionPlan, kidsPlan } = availableRelatedOffers;

  const { selectedPlans } = useSelectedPlans();

  const getRenderedStep = (step: PlanConfigurationStep) => {
    if (step === 'ADD_RELATED_OFFERS') {
      if (!companionPlan) {
        return 'SELECT_QUANTITY_BEFORE_ELIGIBLE_MESSAGE';
      }
      if (kidsPlan) {
        return 'SHOW_ELIGIBLE_MESSAGE';
      }
      return 'ADD_COMPANION_PLAN_ONLY';
    }
    return step;
  };

  const stepForRender = getRenderedStep(step);

  useEffect(() => {
    const title = getStepLayerTitle(stepForRender);
    if (title) {
      setLayerTitle(title);
    }
  }, [stepForRender, setLayerTitle, availableRelatedOffers]);

  switch (stepForRender) {
    case 'SELECT_QUANTITY_BEFORE_ELIGIBLE_MESSAGE':
      return (
        <SelectQuantityStep
          planCardsProducts={planCardsProducts}
          selectedPlan={selectedPlan}
          isAddingBundles={isAddingBundles}
          nextButtonText="Next"
          onNext={() => handleNavigate('SHOW_ELIGIBLE_MESSAGE')}
          onCancel={onCancel}
        />
      );
    case 'ADD_COMPANION_PLAN_ONLY':
      return (
        <AddCompanionsStep
          selectedPlan={selectedPlan}
          companionPlan={companionPlan!}
          isAddingBundles={isAddingBundles}
          onNext={handleNavigateToSelectCompanionQuantity}
          onSkip={addSelectedPlanToCart}
        />
      );

    case 'SHOW_ELIGIBLE_MESSAGE':
      return (
        <EligibleMessageStep
          companionPlan={companionPlan}
          kidsPlan={kidsPlan!}
          selectedPlan={selectedPlan}
          isAddingBundles={isAddingBundles}
          onNext={
            companionPlan
              ? handleNavigateToSelectCompanionQuantity
              : handleNavigateToSelectKidsPlanQuantity
          }
          onSkip={addSelectedPlanToCart}
        />
      );
    case 'SELECT_KIDS_PLAN_QUANTITY':
      return (
        <SelectKidPlanQuantityStep
          kidsPlan={kidsPlan!}
          planCardsProducts={planCardsProducts}
          isAddingBundles={isAddingBundles}
          isLastQuantityStep={false}
          defaultQuantity={
            selectedPlans.find(({ offerId }) => offerId === kidsPlan?.id)
              ?.quantity
          }
          onNext={handleNavigateToReviewRelatedOffersSelection}
          onSkip={
            companionPlan
              ? handleNavigateToReviewRelatedOffersSelection
              : addSelectedPlanToCart
          }
        />
      );
    case 'SELECT_COMPANION_QUANTITY':
      return (
        <SelectCompanionQuantityStep
          companionPlan={companionPlan as RelatedOfferDetails}
          planCardsProducts={planCardsProducts}
          isAddingBundles={isAddingBundles}
          defaultQuantity={
            selectedPlans.find(({ offerId }) => offerId === companionPlan?.id)
              ?.quantity
          }
          isLastQuantityStep={!kidsPlan}
          onNext={
            kidsPlan
              ? handleNavigateToSelectKidsPlanQuantity
              : handleNavigateToReviewRelatedOffersSelection
          }
          onSkip={
            kidsPlan
              ? handleNavigateToSelectKidsPlanQuantity
              : addSelectedPlanToCart
          }
        />
      );
    case 'REVIEW_RELATED_OFFERS_SELECTION':
      return (
        <ReviewSelectionStep
          plans={Object.values(availableRelatedOffers)}
          planCardsProducts={planCardsProducts}
          onBack={() => handleNavigate(previousStep.current!)}
        />
      );
    default:
      return null;
  }
};

export default PrimaryPlanStep;
